import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NotificationBellMenu } from '@mlm/ui';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../lib/api';

/**
 * JM-EX9 bell, re-wired in LMS Phase 2 to the shared `@mlm/ui` menu so the
 * member, creator and admin SPAs cannot drift apart. Everything here is the
 * data half: the same query keys, the same 60s poll + refetch-on-focus, and
 * the same prefix invalidation the bell has always used.
 *
 * No Socket.IO live push yet — the worker doesn't share an IO instance with
 * the API in the current deployment. The 60s poll covers the realistic gap.
 *
 * New in Phase 2: rows whose `metadata.path` is set navigate there (LMS bells
 * always carry one). Rows without a path behave exactly as before — mark read,
 * stay put — which is every pre-Phase-2 kind.
 */
export function NotificationsBell({ compact = false }: { compact?: boolean } = {}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const recentQ = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => listNotifications({ limit: 10 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return (
    <NotificationBellMenu
      items={recentQ.data?.items ?? []}
      unreadCount={unreadQ.data?.count ?? 0}
      onMarkRead={(id) => markOne.mutate(id)}
      onMarkAll={() => markAll.mutate()}
      markAllPending={markAll.isPending}
      onOpen={(path) => navigate(path)}
      seeAllHref="/notifications"
      compact={compact}
    />
  );
}
